import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { notification } from 'antd';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { CREATE_HUBSPOT_AUTH_URL, DISCONNECT_HUBSPOT } from 'graphql/mutations/integrationMutation';
import IntegrationItem from '../IntegrationItem';
import images from 'constants/images';
import utils from 'utils/utils';
import { userVar, fromAppVar } from 'graphql/cache';

const HubSpotIntegration = ({ data, isModalItem, userRole, refetchUser, user }) => {
  const [integrationData, setIntegrationData] = useState(data);
  const [loading, setLoading] = useState();
  const [doRefetch, setDoRefetch] = useState(true);
  const history = useHistory();
  const hubspotConnectionStatus = utils.getQueryStringValue('hubspot');

  useEffect(() => {
    if (data) {
      setIntegrationData(data);
    }
  }, [data]);

  useEffect(() => {
    async function checkUser() {
      if (hubspotConnectionStatus === 'connected' && doRefetch) {
        const userData = await refetchUser();
        if (userData) {
          userVar({ ...user, ...userData });
          setIntegrationData(userData.hubspot);
          setLoading(Object.keys(userData?.hubspot || {}).length ? 'connected' : '');
        } else {
          setIntegrationData(user?.hubspot);
          setLoading(Object.keys(user?.hubspot || {}).length ? 'connected' : '');
        }
        setDoRefetch(false);
      } else if (hubspotConnectionStatus === 'failed') {
        setLoading('');
      }
    }
    checkUser();
  }, [hubspotConnectionStatus, refetchUser, user, doRefetch, data]);

  const [createHubspotAuthUrl] = useMutation(CREATE_HUBSPOT_AUTH_URL);

  const handleConnect = async () => {
    setLoading('connecting');
    const resp = await createHubspotAuthUrl();
    await utils.refetchUserOnClosingTab(resp?.data?.createHubspotAuthUrl?.url, undefined, false);
    setTimeout(async () => {
      const userData = await refetchUser();
      if (userData) {
        if (userData._id) {
          userVar({ ...user, ...userData });
          setIntegrationData(userData.hubspot);
          setLoading(Object.keys(userData?.hubspot || {}).length ? 'connected' : '');
        } else {
          userVar({ ...user, ...userData.data.fetchUser });
          setIntegrationData(userData.data.fetchUser.hubspot);
          setLoading(
            Object.keys(userData?.data?.fetchUser?.hubspot || {}).length ? 'connected' : ''
          );
        }
      } else {
        setIntegrationData(user?.hubspot);
      }
    }, 5000);
  };

  const handleClick = () => {
    if (!integrationData?.token || !Object.keys(integrationData?.token).length) {
      // if (fromAppVar() === 'hubspot') {
      //   setLoading('connecting');
      //   return handleConnect();
      // } else {
      //   handleInstall();
      // }
      return handleConnect();
    } else {
      setLoading('disconnecting');
      return disconnectHubSpot();
    }
  };

  const [disconnectHubSpot, { loading: isDisconnectingHubSpot }] = useMutation(DISCONNECT_HUBSPOT, {
    onCompleted: async () => {
      const userData = await refetchUser();
      if (userData) {
        userVar({ ...user, ...userData });
        if (fromAppVar() === 'hubspot') {
          history.push('/invalid');
        }
      }
      // setRemoveLoader(false);
      setLoading('');
      notification.success({
        message: 'HubSpot Disconnected',
        description: 'Your HubSpot is now disconnected from Prospero.',
      });
    },
    onError: () => {
      // setRemoveLoader(false);
      notification.error({
        message: 'HubSpot Disconnected Failed',
        description: 'Your account failed to disconnect try again.',
      });
    },
  });

  let loadingStatus = isDisconnectingHubSpot ? 'disconnecting' : loading ? loading : '';

  return (
    <>
      <IntegrationItem
        type="hubspot"
        name="HubSpot"
        description="Create proposals for contacts, leads, or from any board in HubSpot"
        integrationLink="https://support.goprospero.com/article/show/176593-how-to-integrate-prospero-with-hubspot"
        status={
          loadingStatus || // this can be connecting or disconnecting
          (integrationData?.token && Object.keys(integrationData?.token).length
            ? 'connected'
            : 'disconnected')
        }
        image={images.HUBSPOT_ICON}
        handleClick={handleClick}
        isModalItem={isModalItem}
        userRole={userRole}
      />
    </>
  );
};

HubSpotIntegration.defaultProps = {
  data: null,
  isModalItem: false,
  userRole: '',
  refetchUser: () => {},
};

HubSpotIntegration.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(null),
  ]),
  isModalItem: PropTypes.bool,
  userRole: PropTypes.string,
  refetchUser: PropTypes.func,
};

export default HubSpotIntegration;
